import { ContentfulBlogPost as BaseBlogPost } from '@/utils/types';
import { Box, Heading, Text } from '@everlywell/ui-kit';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useState, useMemo, useEffect } from 'react';

import ResourceCard from './ResourceCard';
import ResourcesFilterBar, {
  ResourceType,
  AudienceType,
  FocusType,
  SortType,
} from './ResourcesFilterBar';

const RESOURCES_QUERY = graphql`
  query ResourcesGridQuery {
    allContentfulBlogPost(sort: { publishDate: DESC }) {
      nodes {
        id
        title
        slug
        contentType
        audience
        focus
        publishDate
        headerContent {
          headerContent
        }
        pdfAttachment {
          url
          title
          description
        }
        featuredImage {
          gatsbyImageData(
            width: 600
            height: 400
            placeholder: BLURRED
            formats: [AUTO, WEBP]
            layout: CONSTRAINED
          )
          title
          description
        }
      }
    }
  }
`;

export interface ContentfulBlogPost extends BaseBlogPost {
  contentType?: ResourceType | string;
  audience?: AudienceType;
  focus?: FocusType;
  ctaText?: string;
}

interface BlogPostQueryResult {
  allContentfulBlogPost: {
    nodes: Array<ContentfulBlogPost>;
  };
}

// Helper function to create a unique identifier for a resource
const getResourceKey = (resource: ContentfulBlogPost): string =>
  // Use a combination of properties to create a unique key
  `${resource.id}-${resource.title}-${resource.publishDate}`;

export const ResourcesGrid: React.FC = () => {
  const [type, setType] = useState<ResourceType>('All');
  const [audience, setAudience] = useState<AudienceType>('All');
  const [focus, setFocus] = useState<FocusType>('All');
  const [sort, setSort] = useState<SortType>('Latest');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [visibleResources, setVisibleResources] = useState<
    ContentfulBlogPost[]
  >([]);

  const data = useStaticQuery<BlogPostQueryResult>(RESOURCES_QUERY);
  const allResources = useMemo(() => {
    try {
      // Process the data
      if (
        !data?.allContentfulBlogPost?.nodes ||
        data.allContentfulBlogPost.nodes.length === 0
      ) {
        return [];
      }

      return data.allContentfulBlogPost.nodes.map((post) => {
        const defaultAudience = post.audience || ('All' as AudienceType);
        const defaultFocus = post.focus || ('All' as FocusType);
        const defaultCtaText = 'Download';

        return {
          id: post.id,
          title: post.title,
          slug: post.slug,
          contentType: post.contentType,
          audience: defaultAudience,
          focus: defaultFocus,
          publishDate: post.publishDate,
          featuredImage: post.featuredImage,
          ctaText: defaultCtaText,
          headerContent: post.headerContent,
          pdfAttachment: post.pdfAttachment,
        };
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to process resource data'),
      );
      return [];
    }
  }, [data?.allContentfulBlogPost?.nodes]);

  // Filtered resources - moved to useMemo for better performance
  const filteredResources = useMemo(() => {
    try {
      setIsLoading(true);

      if (allResources.length === 0) {
        return [];
      }

      // Filter resources
      const filtered = allResources.filter((resource) => {
        // Type filter
        const typeMatch = type === 'All' || resource.contentType === type;

        // Audience filter
        const audienceMatch =
          audience === 'All' || resource.audience === audience;

        // Focus filter
        const focusMatch = focus === 'All' || resource.focus === focus;

        return typeMatch && audienceMatch && focusMatch;
      });

      // Apply sorting
      const sortedResult = [...filtered];
      if (sort === 'Oldest') {
        sortedResult.sort(
          (a, b) =>
            new Date(a.publishDate).getTime() -
            new Date(b.publishDate).getTime(),
        );
      } else if (sort === 'A-Z') {
        sortedResult.sort((a, b) => a.title.localeCompare(b.title));
      } else if (sort === 'Z-A') {
        sortedResult.sort((a, b) => b.title.localeCompare(a.title));
      } else {
        // Latest is default
        sortedResult.sort(
          (a, b) =>
            new Date(b.publishDate).getTime() -
            new Date(a.publishDate).getTime(),
        );
      }

      return sortedResult;
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to filter resources'),
      );
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [type, audience, focus, sort, allResources]);

  // Update visible resources when filters change
  useEffect(() => {
    setVisibleResources(filteredResources);
  }, [filteredResources]);

  // Get unique types, audiences, and focuses using useMemo
  const uniqueTypes = useMemo(
    () =>
      Array.from(
        new Set(allResources.map((resource) => resource.contentType)),
      ).filter((typ) => typ !== 'All') as ResourceType[],
    [allResources],
  );

  // Get unique audiences from resources for the filter dropdown
  const uniqueAudiences = useMemo(
    () =>
      Array.from(
        new Set(allResources.map((resource) => resource.audience)),
      ).filter((aud) => aud !== 'All') as AudienceType[],
    [allResources],
  );

  // Get unique focuses from resources for the filter dropdown
  const uniqueFocuses = useMemo(
    () =>
      Array.from(
        new Set(allResources.map((resource) => resource.focus)),
      ).filter((foc) => foc !== 'All') as FocusType[],
    [allResources],
  );

  // Error state
  if (error) {
    return (
      <Box maxWidth="1440px" mx="auto" px={[4, 4, 4]} py={8}>
        <Box textAlign="center" py={10}>
          <Heading as="h3" size="lg" mb={4} color="red.500">
            Error loading resources
          </Heading>
          <Text>{error.message}</Text>
        </Box>
      </Box>
    );
  }

  // Show loading state
  if (isLoading && allResources.length > 0) {
    return (
      <Box maxWidth="1440px" mx="auto" px={[4, 4, 4]} py={8} textAlign="center">
        <Text>Loading resources...</Text>
      </Box>
    );
  }

  // Show empty state if no resources are available
  if (!isLoading && visibleResources.length === 0) {
    return (
      <Box maxWidth="1440px" mx="auto" px={[4, 4, 4]} py={8}>
        {/* Filter section */}
        <ResourcesFilterBar
          type={type}
          setType={setType}
          audience={audience}
          setAudience={setAudience}
          focus={focus}
          setFocus={setFocus}
          sort={sort}
          setSort={setSort}
          uniqueTypes={uniqueTypes}
          uniqueAudiences={uniqueAudiences}
          uniqueFocuses={uniqueFocuses}
        />

        <Box textAlign="center" py={10}>
          <Heading as="h3" size="lg" mb={4}>
            No resources found
          </Heading>
          <Text>
            Try adjusting your filters or check back later for new content.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box maxWidth="1440px" mx="auto" px={[4, 4, 4]} py={8}>
      {/* Filter section */}
      <ResourcesFilterBar
        type={type}
        setType={setType}
        audience={audience}
        setAudience={setAudience}
        focus={focus}
        setFocus={setFocus}
        sort={sort}
        setSort={setSort}
        uniqueTypes={uniqueTypes}
        uniqueAudiences={uniqueAudiences}
        uniqueFocuses={uniqueFocuses}
      />

      {/* Resources grid */}
      <Box
        display="grid"
        gridTemplateColumns={['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)']}
        gap={6}
        width="100%"
        justifyItems="center"
        mt={[6, 6, 8]}
        role="region"
        aria-label="Resources grid"
        maxWidth="1440px"
        mx="auto"
      >
        {visibleResources.map((resource) => {
          const resourceKey = getResourceKey(resource);
          return (
            <Box
              key={resourceKey}
              width="100%"
              mt={[4, 4, 0]}
              position="relative"
            >
              <ResourceCard resource={resource} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ResourcesGrid;
